/**
 * Cart Bridge — cross-domain cart hand-off between sister storefronts
 * (heroarts.com <-> spellbinders.com, same Shopify store backend).
 *
 * How it works (simple / v1 — URL hand-off only, no external service):
 *
 * 1. SENDER: when the shopper activates any link pointing at the sister
 *    domain, we decorate that link's URL (at interaction time, so it is
 *    always current) with:
 *      - xd_cart:  the shopper's current cart token (from the `cart` cookie)
 *      - xd_items: a compact snapshot of line items "variantId:qty[:sellingPlanId]"
 *
 * 2. RECEIVER: on page load, if those params are present we strip them from
 *    the URL immediately, then:
 *      - if the local cart is EMPTY  -> adopt the incoming cart token by
 *        writing it into the first-party `cart` cookie (both domains then
 *        share ONE cart from that point on — edits on either side hit the
 *        same cart). If Shopify rejects the copied token, fall back to
 *        rebuilding the cart from the xd_items snapshot via /cart/add.js.
 *      - if the local cart has items -> merge: per variant, quantity becomes
 *        max(local, incoming). Idempotent, so bouncing between domains
 *        doesn't double quantities.
 *
 * Known limits of v1 (by design):
 *  - Only syncs when the shopper navigates through a link on the page.
 *    Typing the other domain directly starts a fresh visit with no hand-off.
 *  - The xd_items fallback carries variant id, quantity and selling plan id,
 *    but not line-item properties (the token-adoption path preserves
 *    everything, and runs whenever the receiving cart is empty).
 *
 * Config comes from window.CartBridgeConfig (see snippets/cart-bridge.liquid).
 */
(function () {
  'use strict';

  var cfg = window.CartBridgeConfig || {};
  var PAIRS = cfg.domainPairs || [['heroarts.com', 'spellbinders.com']];
  var DEBUG = !!cfg.debug;

  var PARAM_TOKEN = 'xd_cart';
  var PARAM_ITEMS = 'xd_items';
  var STORAGE_KEY = 'cart-bridge:applied';
  var MAX_ITEMS = 60; // keep URLs a sane length

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[cart-bridge]'].concat([].slice.call(arguments)));
    }
  }

  /* ------------------------------------------------------------------ *
   * Domain helpers
   * ------------------------------------------------------------------ */

  // "www.spellbinders.com" -> "spellbinders.com"
  function registrableDomain(hostname) {
    var parts = String(hostname || '').toLowerCase().split('.');
    return parts.slice(-2).join('.');
  }

  var myRoot = registrableDomain(location.hostname);
  var sisterRoot = null;
  for (var i = 0; i < PAIRS.length; i++) {
    if (PAIRS[i][0] === myRoot) sisterRoot = PAIRS[i][1];
    else if (PAIRS[i][1] === myRoot) sisterRoot = PAIRS[i][0];
    if (sisterRoot) break;
  }

  if (!sisterRoot) {
    // Not running on one of the configured domains (e.g. .myshopify.com
    // preview or the theme editor) — do nothing.
    log('host', location.hostname, 'is not part of a configured pair; idle.');
    return;
  }

  /* ------------------------------------------------------------------ *
   * Cart cookie + snapshot helpers
   * ------------------------------------------------------------------ */

  function getCartToken() {
    var m = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
    if (!m) return null;
    try {
      return decodeURIComponent(m[1]);
    } catch (e) {
      return m[1];
    }
  }

  // Cart tokens can carry a "?key=..." suffix; compare the stable part.
  function tokenId(token) {
    return String(token || '').split('?')[0];
  }

  function fetchCart() {
    return fetch('/cart.js?xdcb=' + Date.now(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (r) {
      if (!r.ok) throw new Error('cart.js ' + r.status);
      return r.json();
    });
  }

  // Cached copy of the cart used to build xd_items at link-activation time
  // (we cannot await a network call inside a click handler).
  var cartSnapshot = null;

  function refreshSnapshot() {
    fetchCart()
      .then(function (c) {
        cartSnapshot = c;
        log('snapshot refreshed:', c.item_count, 'item(s)');
      })
      .catch(function () {});
  }

  // Refresh the snapshot after any AJAX cart mutation the theme performs.
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var origFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var url = '';
    try {
      url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
    } catch (e) {}
    var p = origFetch.apply(this, args);
    if (CART_MUTATION_RE.test(url)) {
      p.then(function (res) {
        if (res && res.ok) refreshSnapshot();
        return res;
      }).catch(function () {});
    }
    return p;
  };

  /* ------------------------------------------------------------------ *
   * Item snapshot encoding: "variantId:qty[:sellingPlanId],..."
   * ------------------------------------------------------------------ */

  function encodeItems(items) {
    return items
      .slice(0, MAX_ITEMS)
      .map(function (it) {
        var s = it.id + ':' + it.quantity;
        if (it.selling_plan_allocation && it.selling_plan_allocation.selling_plan) {
          s += ':' + it.selling_plan_allocation.selling_plan.id;
        }
        return s;
      })
      .join(',');
  }

  function decodeItems(raw) {
    if (!raw) return [];
    var out = [];
    raw.split(',').forEach(function (chunk) {
      var bits = chunk.split(':');
      var id = parseInt(bits[0], 10);
      var qty = parseInt(bits[1], 10);
      if (!id || !qty || qty < 0) return;
      var item = { id: id, quantity: qty };
      var sp = parseInt(bits[2], 10);
      if (sp) item.selling_plan = sp;
      out.push(item);
    });
    return out;
  }

  /* ------------------------------------------------------------------ *
   * "Already applied" guard (prevents re-applying the same incoming cart
   * on back/forward navigation or domain ping-pong).
   * ------------------------------------------------------------------ */

  function getApplied() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function markApplied(key) {
    if (!key) return;
    try {
      var list = getApplied();
      list.push(key);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(-10)));
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ *
   * SENDER — decorate sister-domain links at interaction time
   * ------------------------------------------------------------------ */

  function decorateLink(anchor) {
    var url;
    try {
      url = new URL(anchor.href, location.href);
    } catch (e) {
      return;
    }
    if (registrableDomain(url.hostname) !== sisterRoot) return;
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return;

    var token = getCartToken();
    var hasItems = cartSnapshot && cartSnapshot.items && cartSnapshot.items.length > 0;

    // Nothing worth carrying over.
    if (!token) return;
    if (cartSnapshot && !hasItems) return; // known-empty cart

    url.searchParams.set(PARAM_TOKEN, token);
    if (hasItems) {
      url.searchParams.set(PARAM_ITEMS, encodeItems(cartSnapshot.items));
    } else {
      url.searchParams.delete(PARAM_ITEMS);
    }
    anchor.href = url.toString();
    log('decorated link ->', anchor.href);
  }

  function onInteraction(e) {
    var a = e.target && e.target.closest && e.target.closest('a[href]');
    if (a) decorateLink(a);
  }

  // pointerdown covers left/middle mouse + touch before navigation;
  // click covers keyboard activation; contextmenu covers "open in new tab".
  ['pointerdown', 'click', 'contextmenu'].forEach(function (evt) {
    document.addEventListener(evt, onInteraction, true);
  });

  /* ------------------------------------------------------------------ *
   * RECEIVER — apply an incoming cart on arrival
   * ------------------------------------------------------------------ */

  function cleanUrl() {
    try {
      var url = new URL(location.href);
      url.searchParams.delete(PARAM_TOKEN);
      url.searchParams.delete(PARAM_ITEMS);
      history.replaceState(history.state, '', url.toString());
    } catch (e) {}
  }

  function writeCartCookie(token) {
    var expires = new Date(Date.now() + 14 * 864e5).toUTCString();
    document.cookie =
      'cart=' + encodeURIComponent(token) +
      '; expires=' + expires +
      '; path=/; domain=.' + myRoot +
      '; Secure; SameSite=Lax';
  }

  function finish(appliedKey, changed) {
    markApplied(appliedKey);
    if (changed) {
      // Reload once (URL is already clean) so header count, drawer and any
      // cart sections render the synced cart.
      location.reload();
    }
  }

  function rebuildFromItems(items, appliedKey) {
    if (!items.length) return;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add.js ' + r.status);
        log('rebuilt cart from item snapshot');
        finish(appliedKey, true);
      })
      .catch(function (err) {
        log('rebuild failed', err);
        markApplied(appliedKey); // don't retry-loop on a broken payload
      });
  }

  function adoptToken(token, fallbackItems, appliedKey) {
    writeCartCookie(token);
    fetchCart()
      .then(function (c) {
        if (c.item_count > 0 && tokenId(c.token) === tokenId(token)) {
          // Both domains now point at the SAME cart. Best case.
          log('adopted sister cart token; carts are unified');
          finish(appliedKey, true);
        } else {
          log('token adoption rejected; falling back to item rebuild');
          rebuildFromItems(fallbackItems, appliedKey);
        }
      })
      .catch(function () {});
  }

  function mergeItems(localCart, incoming, appliedKey) {
    var localByVariant = {};
    (localCart.items || []).forEach(function (it) {
      localByVariant[it.id] = (localByVariant[it.id] || 0) + it.quantity;
    });

    var toAdd = [];
    var toRaise = {};
    incoming.forEach(function (it) {
      var localQty = localByVariant[it.id] || 0;
      if (localQty === 0) {
        toAdd.push(it);
      } else if (it.quantity > localQty) {
        // quantity = max(local, incoming) -> idempotent merge
        toRaise[it.id] = it.quantity;
      }
    });

    if (!toAdd.length && !Object.keys(toRaise).length) {
      log('merge: nothing to do');
      finish(appliedKey, false);
      return;
    }

    var addStep = toAdd.length
      ? fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: toAdd }),
        })
      : Promise.resolve();

    addStep
      .then(function () {
        if (!Object.keys(toRaise).length) return;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: toRaise }),
        });
      })
      .then(function () {
        log('merged incoming cart (added ' + toAdd.length + ', raised ' + Object.keys(toRaise).length + ')');
        finish(appliedKey, true);
      })
      .catch(function (err) {
        log('merge failed', err);
        markApplied(appliedKey);
      });
  }

  function receive() {
    var params;
    try {
      params = new URLSearchParams(location.search);
    } catch (e) {
      return;
    }
    var token = params.get(PARAM_TOKEN);
    var itemsRaw = params.get(PARAM_ITEMS);
    if (!token && !itemsRaw) return;

    // Strip params right away so refresh / copy-link / back-forward
    // don't re-trigger the hand-off.
    cleanUrl();

    // Key the guard on token AND item snapshot: the token is stable for the
    // life of a cart, so keying on it alone would permanently block future
    // hand-offs after one merge, even when the sister cart's contents have
    // since changed. Including xd_items means a changed cart state produces
    // a fresh key, while an identical re-delivery (back/forward, ping-pong)
    // is still skipped.
    var appliedKey = (token ? tokenId(token) : 'token:none') + '|' + (itemsRaw || '');
    if (getApplied().indexOf(appliedKey) !== -1) {
      log('incoming cart state already applied; skipping');
      return;
    }

    var incoming = decodeItems(itemsRaw);

    fetchCart()
      .then(function (local) {
        if (token && tokenId(local.token) === tokenId(token)) {
          log('local cart already IS the incoming cart');
          markApplied(appliedKey);
          return;
        }
        if (local.item_count === 0 && token) {
          adoptToken(token, incoming, appliedKey);
        } else if (local.item_count === 0 && incoming.length) {
          rebuildFromItems(incoming, appliedKey);
        } else if (incoming.length) {
          mergeItems(local, incoming, appliedKey);
        } else {
          markApplied(appliedKey);
        }
      })
      .catch(function (err) {
        log('receive failed', err);
      });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  function boot() {
    receive();
    refreshSnapshot();
    // Also refresh the snapshot via the theme's pubsub, when available
    // (constants.js + pubsub.js are loaded before this file).
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, refreshSnapshot);
      }
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
